import { z } from 'zod';

// Electronic signature module (AES, eIDAS Art. 26). Shared contracts between the
// Hono API and the React SPA.

export const DOCUMENT_STATUSES = ['draft', 'sent', 'viewed', 'signed', 'expired'] as const;
export const DocumentStatusSchema = z.enum(DOCUMENT_STATUSES);
export type DocumentStatus = z.infer<typeof DocumentStatusSchema>;

export const SIGNATURE_EVENT_TYPES = [
  'sent',
  'opened',
  'viewed',
  'otp_requested',
  'otp_validated',
  'signed',
] as const;
export const SignatureEventTypeSchema = z.enum(SIGNATURE_EVENT_TYPES);
export type SignatureEventType = z.infer<typeof SignatureEventTypeSchema>;

// E.164: leading '+', country digit 1-9, up to 15 digits total.
export const PhoneE164Schema = z
  .string()
  .regex(/^\+[1-9]\d{1,14}$/, 'Phone must be E.164 (e.g. "+34611222333")');

export const SignerInputSchema = z.object({
  fullName: z.string().min(1).max(200),
  email: z.string().email(),
  phone: PhoneE164Schema,
});
export type SignerInput = z.infer<typeof SignerInputSchema>;

// Document creation metadata. The PDF itself travels as a multipart file part;
// this object is the JSON `meta` part validated alongside it.
export const CreateDocumentSchema = z.object({
  filename: z.string().min(1).max(255),
  signers: z.array(SignerInputSchema).min(1).max(10),
  // Days until the magic links (and the document) expire. Defaults applied API-side.
  expiresInDays: z.number().int().min(1).max(90).optional(),
});
export type CreateDocumentRequest = z.infer<typeof CreateDocumentSchema>;

// --- Placed form fields (v2 designer) -------------------------------------

export const FIELD_TYPES = ['signature', 'date', 'text'] as const;
export const FieldTypeSchema = z.enum(FIELD_TYPES);
export type FieldType = z.infer<typeof FieldTypeSchema>;

const Fraction = z.number().min(0).max(1);

// One placed field, as saved by the designer. Geometry is fractional [0,1].
export const DocumentFieldInputSchema = z.object({
  signerId: z.string().uuid(),
  type: FieldTypeSchema,
  page: z.number().int().min(0).max(1000),
  x: Fraction,
  y: Fraction,
  w: z.number().min(0.005).max(1),
  h: z.number().min(0.005).max(1),
  required: z.boolean().default(true),
});
export type DocumentFieldInput = z.infer<typeof DocumentFieldInputSchema>;

export const SaveFieldsSchema = z.object({
  fields: z.array(DocumentFieldInputSchema).max(200),
});
export type SaveFieldsRequest = z.infer<typeof SaveFieldsSchema>;

export type DocumentFieldView = {
  id: string;
  signerId: string;
  type: FieldType;
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
  required: boolean;
  value: string | null;
};

// Public signing submission. Requires explicit consent plus the signer's typed
// name and its rasterised signature image (a PNG data URL produced client-side).
// fieldValues carries the text the signer typed into their assigned text fields.
export const SignConsentSchema = z.object({
  consent: z.literal(true),
  signatureName: z.string().min(1).max(120),
  // data:image/png;base64,... — capped to keep uploads sane (~2 MB image).
  signatureImage: z
    .string()
    .regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/, 'PNG data URL expected')
    .max(2_800_000),
  fieldValues: z
    .array(z.object({ fieldId: z.string().uuid(), value: z.string().max(2000) }))
    .max(200)
    .optional(),
});
export type SignConsentRequest = z.infer<typeof SignConsentSchema>;

// ---- Response DTOs (admin) ----

export type SignerSummary = {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  signedAt: string | null;
  tokenExpiresAt: string;
  signingUrl: string | null;
};

export type DocumentSummary = {
  id: string;
  filename: string;
  status: DocumentStatus;
  createdAt: string;
  expiresAt: string | null;
  signedPath: string | null;
  signers: SignerSummary[];
};

export type SignatureEventView = {
  id: number;
  signerId: string;
  eventType: SignatureEventType;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
};

// ---- Response DTOs (public signing page) ----

export type PublicSignerView = {
  documentFilename: string;
  signerFullName: string;
  status: DocumentStatus;
  alreadySigned: boolean;
  expired: boolean;
  // True when SMS OTP must be validated before signing (Phase 2).
  otpRequired: boolean;
};

export const OtpVerifySchema = z.object({
  code: z.string().regex(/^\d{6}$/, '6-digit code expected'),
});
export type OtpVerifyRequest = z.infer<typeof OtpVerifySchema>;
